import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useAuthStatus } from '../../hooks/useAuthStatus';
import { useOrgStore } from '../../store/useOrgStore';
import { PageTransition } from '../ui/PageTransition';

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, loading } = useAuthStatus();
  const reset = useOrgStore((s) => s.reset);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer when the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && status && !status.connected) {
      reset();
      navigate('/connect', { replace: true });
    }
  }, [status, loading, navigate, reset]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500">
        Verifying session…
      </div>
    );
  }
  if (!status?.connected) return <Navigate to="/connect" replace />;
  // Role guard: /internal is for a stamped consultant and nobody else.
  // The callback routes by role, but if anything (closure-captured stale
  // status, manual nav, hard refresh on an /internal/*
  // URL) lands somebody here, this Navigate is the last line of defense. A
  // client admin goes to /hub; a session with no role holds no /internal grant
  // at all since TYR-213, so it goes back to the sign-in page.
  if (status.role !== 'consultant') {
    return <Navigate to={status.role === 'client-admin' ? '/hub' : '/connect'} replace />;
  }

  return (
    <div className="min-h-screen flex bg-surface-0 relative">
      {/* Mobile backdrop */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-30"
        />
      )}

      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onMenuClick={() => setMobileNavOpen(true)} />
        <main className="flex-1 p-6 lg:p-10 overflow-x-hidden">
          <AnimatePresence mode="wait">
            <PageTransition key={location.pathname}>
              <Outlet />
            </PageTransition>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
